import { useEffect, useRef } from 'react';

export const useResizeObserver = (
  textRef: React.RefObject<Element>,
  onResize: (width: number, height: number) => void
) => {
  const observedSizeRef = useRef<{ width?: number; height?: number }>({});

  const handleResize = () => {
    const newObservedSize = {
      width: textRef.current?.clientWidth,
      height: textRef.current?.clientHeight,
    };

    if (
      newObservedSize.width !== observedSizeRef.current.width ||
      newObservedSize.height !== observedSizeRef.current.height
    ) {
      onResize(newObservedSize.width ?? 0, newObservedSize.height ?? 0);
    }

    observedSizeRef.current = newObservedSize;
  };

  // Try to use ResizeObserver if available. Any browser since 2020 should support it.
  if (globalThis['ResizeObserver']) {
    const resizeObserver = useRef<ResizeObserver>(new ResizeObserver(handleResize));
    useEffect(() => {
      const textElement = textRef.current;
      textElement && resizeObserver.current.observe(textElement);

      return () => {
        textElement && resizeObserver.current.unobserve(textElement);
      };
    });
  }

  // Also observe window resize events, and run after every layout render.
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });
};
